import type {
  Incident,
  IncidentUpdate,
  IncidentSeverity,
  IncidentStatus,
  IncidentCategory,
  IncidentMetrics,
  AutoRecoveryAction,
  CircuitBreakerState
} from '@my-roadtrip/types'
import { db } from '../core/database/firebase.js'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('IncidentManager')

// Circuit breakers per service
const circuitBreakers = new Map<string, CircuitBreakerState>()

// Active health checks
let healthCheckIntervals: NodeJS.Timeout[] = []

class IncidentManagerService {
  /**
   * Create a new incident
   */
  async createIncident(params: {
    title: string
    description: string
    severity: IncidentSeverity
    category: IncidentCategory
    affectedServices: string[]
    affectedUsers?: number
    reportedBy?: string
    metadata?: Record<string, unknown>
  }): Promise<Incident> {
    const incident: Incident = {
      id: `inc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ...params,
      affectedUsers: params.affectedUsers || 0,
      status: 'open',
      createdAt: new Date(),
      updatedAt: new Date()
    }

    await db.collection('incidents').doc(incident.id).set({
      ...incident,
      createdAt: incident.createdAt,
      updatedAt: incident.updatedAt
    })

    logger.warn(`[Incident Created] ${incident.id} - ${incident.title}`, {
      severity: incident.severity,
      category: incident.category,
      services: incident.affectedServices
    })

    // Check if auto-recovery should trigger
    await this.triggerAutoRecovery(incident)

    return incident
  }

  /**
   * Update incident status
   */
  async updateIncident(
    incidentId: string,
    update: {
      status?: IncidentStatus
      severity?: IncidentSeverity
      assignedTo?: string
      resolvedAt?: Date
      closedAt?: Date
      metadata?: Record<string, unknown>
    }
  ): Promise<void> {
    const updateData: any = {
      ...update,
      updatedAt: new Date()
    }

    await db.collection('incidents').doc(incidentId).update(updateData)

    logger.info(`[Incident Updated] ${incidentId}`, update)
  }

  /**
   * Add update/comment to incident
   */
  async addIncidentUpdate(params: {
    incidentId: string
    status: IncidentStatus
    message: string
    createdBy: string
    isPublic: boolean
    actions?: string[]
  }): Promise<IncidentUpdate> {
    const update: IncidentUpdate = {
      id: `upd-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ...params,
      createdAt: new Date()
    }

    await db.collection('incident_updates').doc(update.id).set({
      ...update,
      createdAt: update.createdAt
    })

    // Update incident status
    await this.updateIncident(params.incidentId, { 
      status: params.status,
      ...(params.status === 'resolved' ? { resolvedAt: new Date() } : {}),
      ...(params.status === 'closed' ? { closedAt: new Date() } : {})
    })

    return update
  }

  /**
   * Get incident metrics
   */
  async getMetrics(period: 'day' | 'week' | 'month' = 'month'): Promise<IncidentMetrics> {
    const now = new Date()
    let startDate: Date

    switch (period) {
      case 'day':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        break
    }

    const snapshot = await db.collection('incidents')
      .where('createdAt', '>=', startDate)
      .where('createdAt', '<=', now)
      .get()

    const incidents = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Incident[]

    const total = incidents.length
    const open = incidents.filter(i => i.status !== 'closed' && i.status !== 'resolved').length
    const critical = incidents.filter(i => i.severity === 'critical').length

    // Calculate mean time to resolve
    const resolvedIncidents = incidents.filter(i => i.resolvedAt)
    const meanTimeToResolve = resolvedIncidents.length > 0
      ? resolvedIncidents.reduce((sum, i) => {
          const duration = i.resolvedAt!.getTime() - i.createdAt.getTime()
          return sum + duration / (1000 * 60) // Convert to minutes
        }, 0) / resolvedIncidents.length
      : 0

    // By category
    const byCategory: Record<IncidentCategory, number> = {
      api: 0,
      database: 0,
      performance: 0,
      security: 0,
      billing: 0,
      infrastructure: 0,
      'user-reported': 0
    }
    for (const incident of incidents) {
      byCategory[incident.category] = (byCategory[incident.category] || 0) + 1
    }

    // By severity
    const bySeverity: Record<IncidentSeverity, number> = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    }
    for (const incident of incidents) {
      bySeverity[incident.severity] = (bySeverity[incident.severity] || 0) + 1
    }

    // Recent incidents (last 5)
    const sortedIncidents = [...incidents].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    const recentIncidents = sortedIncidents.slice(0, 5)

    return {
      total,
      open,
      critical,
      meanTimeToResolve,
      meanTimeToDetect: 0, // TODO: Calculate
      byCategory,
      bySeverity,
      recentIncidents,
      trend: {
        current: total,
        previous: 0, // TODO: Get previous period
        change: 0
      }
    }
  }

  /**
   * Trigger auto-recovery for incident
   */
  private async triggerAutoRecovery(incident: Incident): Promise<void> {
    // Get recovery actions for affected services
    const snapshot = await db.collection('auto_recovery_actions')
      .where('enabled', '==', true)
      .get()

    const actions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AutoRecoveryAction[]

    for (const action of actions) {
      if (incident.affectedServices.includes(action.action.target)) {
        logger.info(`[Auto-Recovery] Triggering ${action.type} for ${action.action.target}`)
        
        try {
          await this.executeRecoveryAction(action, incident)
          
          await this.addIncidentUpdate({
            incidentId: incident.id,
            status: 'monitoring',
            message: `Auto-recovery action triggered: ${action.type}`,
            createdBy: 'system',
            isPublic: false,
            actions: [action.type]
          })
        } catch (error) {
          logger.error('[Auto-Recovery] Failed', { error, action: action.type })
        }
      }
    }
  }

  /**
   * Execute a recovery action
   */
  private async executeRecoveryAction(
    action: AutoRecoveryAction,
    incident: Incident
  ): Promise<void> {
    // Check cooldown
    if (action.lastTriggered) {
      const elapsed = Date.now() - action.lastTriggered.getTime()
      if (elapsed < action.action.cooldown * 1000) {
        logger.info('[Auto-Recovery] Action in cooldown', {
          action: action.type,
          remaining: (action.action.cooldown * 1000 - elapsed) / 1000
        })
        return
      }
    }

    switch (action.type) {
      case 'clear-cache':
        // TODO: Implement cache clearing
        logger.info('[Auto-Recovery] Cache cleared', { target: action.action.target })
        break

      case 'circuit-breaker':
        this.openCircuitBreaker(action.action.target)
        break

      case 'rate-limit':
        // TODO: Implement rate limiting adjustment
        logger.info('[Auto-Recovery] Rate limit applied', { target: action.action.target })
        break

      default:
        logger.warn('[Auto-Recovery] Unknown action type', { type: action.type })
    }

    // Update action stats
    await db.collection('auto_recovery_actions').doc(action.id).update({
      lastTriggered: new Date(),
      executionCount: (action.executionCount || 0) + 1
    })
  }

  /**
   * Open circuit breaker for a service
   */
  private openCircuitBreaker(service: string): void {
    const breaker: CircuitBreakerState = {
      service,
      state: 'open',
      failureCount: 0,
      failureThreshold: 5,
      timeout: 60000, // 1 minute
      lastFailure: new Date(),
      nextRetry: new Date(Date.now() + 60000),
      successCount: 0
    }

    circuitBreakers.set(service, breaker)

    // Schedule retry
    setTimeout(() => {
      this.halfOpenCircuitBreaker(service)
    }, breaker.timeout)

    logger.warn(`[Circuit Breaker] Opened for ${service}`)
  }

  /**
   * Move circuit breaker to half-open state
   */
  private halfOpenCircuitBreaker(service: string): void {
    const breaker = circuitBreakers.get(service)
    if (breaker?.state === 'open') {
      breaker.state = 'half-open'
      breaker.successCount = 0
      circuitBreakers.set(service, breaker)
      logger.info(`[Circuit Breaker] Half-open for ${service}`)
    }
  }

  /**
   * Get circuit breaker state
   */
  getCircuitBreaker(service: string): CircuitBreakerState | undefined {
    return circuitBreakers.get(service)
  }

  /**
   * Record service call result for circuit breaker
   */
  recordServiceCall(service: string, success: boolean, error?: string): void {
    let breaker = circuitBreakers.get(service)

    if (!breaker) {
      breaker = {
        service,
        state: 'closed',
        failureCount: 0,
        failureThreshold: 5,
        timeout: 60000,
        successCount: 0,
        metadata: { recentErrors: [], errorRate: 0 }
      }
      circuitBreakers.set(service, breaker)
    }

    if (success) {
      if (breaker.state === 'half-open') {
        breaker.successCount++
        if (breaker.successCount >= 3) {
          // Close the circuit
          breaker.state = 'closed'
          breaker.failureCount = 0
          logger.info(`[Circuit Breaker] Closed for ${service}`)
        }
      } else if (breaker.state === 'closed') {
        breaker.failureCount = Math.max(0, breaker.failureCount - 1)
      }
    } else {
      breaker.failureCount++
      breaker.lastFailure = new Date()

      if (error && breaker.metadata) {
        breaker.metadata.recentErrors.push(error)
        if (breaker.metadata.recentErrors.length > 10) {
          breaker.metadata.recentErrors.shift()
        }
      }

      if (
        (breaker.state === 'closed' && breaker.failureCount >= breaker.failureThreshold) ||
        breaker.state === 'half-open'
      ) {
        this.openCircuitBreaker(service)
      }
    }

    circuitBreakers.set(service, breaker)
  }

  /**
   * Get all incidents
   */
  async getIncidents(filters?: {
    status?: IncidentStatus
    severity?: IncidentSeverity
    category?: IncidentCategory
    limit?: number
  }): Promise<Incident[]> {
    let query: FirebaseFirestore.Query = db.collection('incidents')

    if (filters?.status) {
      query = query.where('status', '==', filters.status)
    }
    if (filters?.severity) {
      query = query.where('severity', '==', filters.severity)
    }
    if (filters?.category) {
      query = query.where('category', '==', filters.category)
    }

    query = query.orderBy('createdAt', 'desc').limit(filters?.limit || 50)

    const snapshot = await query.get()
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Incident[]
  }

  /**
   * Cleanup on shutdown
   */
  async shutdown(): Promise<void> {
    for (const interval of healthCheckIntervals) {
      clearInterval(interval)
    }
    healthCheckIntervals = []
  }
}

export const incidentManager = new IncidentManagerService()
